// src/lib/queryCache.js
//
// Stale-while-revalidate cache for Supabase LIST queries used by 14 module
// hooks (inbox, deals, projects, contacts, invoices, tasks, …). Callers
// read via getCached(key), render instantly if present, then fetch fresh
// in the background and write via setCached(key, data).
//
// This file used to be a pure in-memory Map that died on page reload. It
// now delegates to entityCache under the 'list' type — which has Stage 2
// IndexedDB write-through via persistentCache. Net effect: list results
// survive reloads AND offline sessions. The whole OS renders cached data
// when the server hiccups; the background refresh kicks in when the
// server is reachable again.
//
// No behavior change for callers: getCached / setCached / hasEverLoaded
// all keep the same signatures.

import { get as cacheGet, set as cacheSet } from './entityCache'

const TYPE = 'list'
const MAX_AGE_MS = 5 * 60 * 1000 // 5 minutes — soft freshness, triggers silent refresh

// Track modules that have loaded data at least once this session so we
// never flash a loading skeleton after the first fetch.
const loadedModules = new Set()

// Entries are stored as { data, ts } so we can keep the "stale but visible"
// semantic — getCached returns the data regardless of age; callers refetch
// silently. Age-awareness is the caller's choice, not the cache's.

// maxAgeMs: optional hard expiry. When provided, entries older than that are
// treated as a miss (returns undefined) so stale derived state doesn't survive
// across deploys or sessions. Module list hooks omit this — they want the
// stale-while-revalidate semantic. Sidebar participant caches should pass a
// short TTL (e.g. 20 min) because they store derived classification results
// that become wrong when the underlying resolution logic changes.
export function getCached(key, { maxAgeMs } = {}) {
  const entry = cacheGet(TYPE, key)
  if (!entry) return undefined
  if (maxAgeMs != null && Date.now() - entry.ts > maxAgeMs) return undefined
  return entry.data
}

// Returns true if this module has EVER successfully loaded data this session.
// Used to suppress loading skeletons after the first fetch.
export function hasEverLoaded(keyPrefix) {
  return loadedModules.has(keyPrefix)
}

export function setCached(key, data) {
  cacheSet(TYPE, key, { data, ts: Date.now() })
  const prefix = (key || '').split(':')[0]
  if (prefix) loadedModules.add(prefix)
}

// Re-export for call sites that want the max-age constant (e.g. to decide
// whether to show a "last updated Xm ago" indicator).
export const CACHE_MAX_AGE_MS = MAX_AGE_MS
